import { useEffect, useState } from "react";
import {
  ReactCompareSlider,
  ReactCompareSliderHandle,
} from "react-compare-slider";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/Reveal";

const imgSrc = (u) =>
  !u || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}/api/files/${u}`;

const spanFor = (i) =>
  i === 0
    ? "lg:col-span-12"
    : i === 3
      ? "lg:col-span-12"
      : i === 1
        ? "lg:col-span-7"
        : "lg:col-span-5";

const heightFor = (i) => (i === 0 || i === 3 ? "h-[520px] md:h-[820px]" : "h-[460px] md:h-[600px]");

function BeforeAfter({ project, index }) {
  const navigate = useNavigate();
  return (
    <Reveal
      className={`grid grid-cols-1 ${spanFor(index)} col-span-1`}
      delay={(index % 2) * 0.1}
    >
      <article
        data-testid={`project-card-${index}`}
        className="group border border-white/10 bg-[#1a1a1a] rounded-[2px] overflow-hidden hover:border-[#c5832b]/50 transition-colors duration-500"
      >
        <div className={`relative ${heightFor(index)}`}>
          <ReactCompareSlider
            data-testid={`project-slider-${index}`}
            itemOne={
              <img
                src={imgSrc(project.before_url)}
                alt={`${project.title} — before`}
                loading="lazy"
                className="w-full h-full object-cover img-treated"
              />
            }
            itemTwo={
              <img
                src={imgSrc(project.after_url)}
                alt={`${project.title} — after`}
                loading="lazy"
                className="w-full h-full object-cover img-treated"
              />
            }
            handle={
              <ReactCompareSliderHandle
                buttonStyle={{
                  background: "#c5832b",
                  border: "none",
                  boxShadow: "0 0 20px rgba(197,131,43,0.45)",
                }}
                linesStyle={{ background: "#c5832b", width: 2 }}
              />
            }
            style={{ width: "100%", height: "100%" }}
          />
          <span className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.2em] font-semibold bg-black/60 backdrop-blur px-3 py-1.5 text-white/80 rounded-[2px]">
            Before
          </span>
          <span className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#c5832b] px-3 py-1.5 text-[#121212] rounded-[2px]">
            After
          </span>
        </div>
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#c5832b] font-semibold mb-2">
              {project.category} · {project.year}
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-[#f5f5f0]">
              {project.title}
            </h3>
            <p className="mt-3 text-sm md:text-base text-[#a3a3a3] leading-relaxed max-w-xl">
              {project.description}
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-start md:items-end gap-3">
            <p className="flex items-center gap-2 text-sm text-[#a3a3a3]">
              <MapPin size={14} className="text-[#c5832b]" />
              {project.location}
            </p>
            <button
              data-testid={`project-view-button-${index}`}
              onClick={() => navigate(`/projects/${project.id}`)}
              className="group/link flex items-center gap-2 text-sm font-semibold text-[#c5832b] hover:text-[#d9953c] transition-colors duration-300"
            >
              View project
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
              />
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoadError(false);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`)
      .then((r) => {
        if (!r.ok) throw new Error("bad status");
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          setProjects(
            data.map(({ id, title, location, category, description, details, before_url, after_url, year }) => ({
              id, title, location, category, description, details, before_url, after_url, year,
            }))
          );
        } else {
          throw new Error("no projects");
        }
      })
      .catch(() => setLoadError(true));
  }, [retryCount]);

  return (
    <section id="projects" data-testid="projects-section" className="py-24 md:py-32 bg-[#1a1a1a] border-y border-white/10">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <Reveal>
            <Eyebrow className="mb-4">Recent work</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight">
              Before &amp; after,
              <br />
              <em className="italic text-[#c5832b]">drag to believe.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-[#a3a3a3] leading-relaxed">
              Drag the copper handle on each photo to reveal the
              transformation. Every project photographed on completion.
            </p>
          </Reveal>
        </div>

        {loadError && (
          <div
            data-testid="projects-load-error"
            className="border border-dashed border-white/15 rounded-[2px] p-12 text-center"
          >
            <p className="text-[#a3a3a3] mb-6">
              Projects couldn't load right now — please try again.
            </p>
            <button
              data-testid="projects-retry-button"
              onClick={() => setRetryCount((c) => c + 1)}
              className="bg-[#c5832b] hover:bg-[#d9953c] text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300"
            >
              Try again
            </button>
          </div>
        )}

        {!projects && !loadError && (
          <div className="h-[420px] md:h-[620px] border border-white/5 rounded-[2px] bg-white/[0.02] animate-pulse" />
        )}

        {projects && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
            {projects.map((p, i) => (
              <BeforeAfter key={p.id || p.title} project={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
